// Source: https://usehooks-ts.com/react-hook/use-local-storage

import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { useEventCallback, useEventListener } from 'usehooks-ts'

declare global {
    interface WindowEventMap {
        'local-storage': CustomEvent
    }
}

type SetValue<T> = Dispatch<SetStateAction<T>>

// Strings are stored as-is (code like `authorizedFetch` reads the API token directly from localStorage), `undefined` as
// the string "undefined" and anything else as JSON.
export const serializeStoredValue = (value: unknown): string => {
    if (value === undefined) {
        return 'undefined'
    }

    return typeof value === 'string' ? value : JSON.stringify(value)
}

// Reverses `serializeStoredValue`. The result is checked with `isValue`, so a stored value of the wrong type falls back
// to `initialValue` instead of being returned under the wrong type. Because strings are stored as-is, a `T` that mixes
// strings with other types (e.g. `string | number`) reads a stored `5` back as the string "5".
export const deserializeStoredValue = <T>(item: string | null, initialValue: T, isValue: (value: unknown) => value is T): T => {
    if (! item) {
        return initialValue
    }

    if (item === 'undefined') {
        const storedUndefined = undefined

        return isValue(storedUndefined) ? storedUndefined : initialValue
    }

    if (isValue(item)) {
        return item
    }

    try {
        const parsed: unknown = JSON.parse(item)

        return isValue(parsed) ? parsed : initialValue
    } catch {
        return initialValue
    }
}

export const useLocalStorage = <T>(key: string, initialValue: T, isValue: (value: unknown) => value is T): [T, SetValue<T>] => {
    // Get from local storage then
    // parse stored value or return initialValue
    const readValue = useCallback((): T => {
        // Prevent build error "window is undefined" but keeps working
        if (typeof window === 'undefined') {
            return initialValue
        }

        try {
            return deserializeStoredValue(window.localStorage.getItem(key), initialValue, isValue)
        } catch (error) {
            console.warn(`Error reading localStorage key “${key}”:`, error)
            return initialValue
        }
    }, [initialValue, isValue, key])

    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T>(readValue)

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue: SetValue<T> = useEventCallback(value => {
        // Prevent build error "window is undefined" but keeps working
        if (typeof window === 'undefined') {
            console.warn(
                `Tried setting localStorage key “${key}” even though environment is not a client`,
            )
        }

        try {
            // Allow value to be a function so we have the same API as useState
            const newValue = value instanceof Function ? value(storedValue) : value

            window.localStorage.setItem(key, serializeStoredValue(newValue))

            // Save state
            setStoredValue(newValue)

            // We dispatch a custom event so every useLocalStorage hook are notified
            window.dispatchEvent(new CustomEvent('local-storage'))
        } catch (error) {
            console.warn(`Error setting localStorage key “${key}”:`, error)
        }
    })

    useEffect(() => {
        setStoredValue(readValue())
    }, [])

    const handleStorageChange = useCallback(
        (event: StorageEvent | CustomEvent) => {
            if (event instanceof StorageEvent && event.key && event.key !== key) {
                return
            }
            setStoredValue(readValue())
        },
        [key, readValue],
    )

    // this only works for other documents, not the current one
    useEventListener('storage', handleStorageChange)

    // this is a custom event, triggered in writeValueToLocalStorage
    // See: useLocalStorage()
    useEventListener('local-storage', handleStorageChange)

    return [storedValue, setValue]
}
